import { Avatar, Button, Stack, Typography } from '@mui/material';
import {
  ISubject,
  ISubCategory,
  IDepartment,
} from 'API/UniversalRequest/universalRequest';
import { TAB_VALUE_TYPE } from '..';
import { SubCategoriesListItem } from './SubCategoriesListItem';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import React, { useEffect, useState } from 'react';
import { CustomMetaTitle } from 'Components';
import { NewRightArrow } from 'Images';

interface ISubCategoriesList {
  categoriesList: {
    subjects: ISubject[];
    departments: IDepartment[];
    subSubjects: ISubCategory[];
  };
  tabValue: TAB_VALUE_TYPE;
  selectedCategoryId: number | null;
  handleSubCategoryClick: (subCategoryId: number) => void;
  goBack: (isReset?: boolean) => void;
  getParentTitleById: string;
  isLoading: boolean;
}

export const SubCategoriesList = ({
  categoriesList,
  tabValue,
  selectedCategoryId,
  handleSubCategoryClick,
  goBack,
  getParentTitleById,
  isLoading,
}: ISubCategoriesList) => {
  const [filteredSubCategories, setFilteredSubCategories] = useState<
    ISubCategory[] | ISubject[]
  >([]);
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const isSubject = tabValue === TAB_VALUE_TYPE.SUBJECTS;

  useEffect(() => {
    if (isSubject) {
      const newFilteredSubCategories: ISubCategory[] = [];
      categoriesList.subjects
        .find((subject: ISubject) => subject.id === selectedCategoryId)
        ?.subSubjectIds.forEach((subSubjectId) => {
          const subSubject = categoriesList.subSubjects.find(
            (subSubject: ISubCategory) => subSubject.id === subSubjectId,
          );

          if (subSubject) newFilteredSubCategories.push(subSubject);
        });
      if (!isLoading && newFilteredSubCategories.length === 0) goBack(true);
      else setFilteredSubCategories(newFilteredSubCategories);
    } else {
      const newFilteredSubCategories =
        categoriesList.departments.find(
          (department) => department.id === selectedCategoryId,
        )?.parentCatagories || [];
      if (!isLoading && newFilteredSubCategories.length === 0) goBack(true);
      else setFilteredSubCategories(newFilteredSubCategories);
    }
  }, [
    categoriesList.departments,
    categoriesList.subSubjects,
    categoriesList.subjects,
    isSubject,
    selectedCategoryId,
    goBack,
    isLoading,
  ]);
  useEffect(() => {
    if (isSubject || !!selectedCategoryId) window.scrollTo(0, 0);
    else goBack(true);
  }, [goBack, isSubject, selectedCategoryId]);

  return (
    <>
      <CustomMetaTitle
        title={`${dictionary.title} ${dictionary.subject} ${getParentTitleById} - ${dictionary.metaTitleStepTwo}`}
        disablePrefix
      />
      <Stack
        component='fieldset'
        aria-describedby='select-parent-description'
        sx={{
          maxWidth: '680px',
          m: '64px auto 0',
          p: isDesktop ? '0px' : '0 24px',
          bgcolor: isDesktop ? '#E7EDFF' : undefined,
          borderRadius: '20px',
          gap: '12px',
        }}
      >
        {isSubject ? (
          <>
            <legend>{dictionary.inWhatMatter}</legend>
            <span className='sr-only' id='select-parent-description'>
              {dictionary.selectParentDescription}
            </span>
            <Typography
              variant={'body1'}
              component='h2'
              sx={{
                mb: '4px',
              }}
            >
              {dictionary.inWhatMatter}
            </Typography>
            {filteredSubCategories?.length > 0 ? (
              filteredSubCategories.map((subSubject) => {
                if (
                  categoriesList.subSubjects.find(
                    (sub) => sub.id === subSubject.id,
                  )?.isHideInClient
                ) {
                  return <React.Fragment key={subSubject.id}></React.Fragment>;
                }
                return (
                  <SubCategoriesListItem
                    key={subSubject.id}
                    subCategory={subSubject as ISubCategory}
                    handleSubCategoryClick={handleSubCategoryClick}
                  />
                );
              })
            ) : (
              <Typography>
                {dictionary.noAvailableDisabilityConfirmations}
              </Typography>
            )}
          </>
        ) : (
          (filteredSubCategories as ISubject[])?.map((subject) => (
            <React.Fragment key={subject.id}>
              <Typography
                component='h2'
                sx={{
                  mt: '34px',
                }}
              >
                {subject.title}
              </Typography>
              {subject.subSubjectIds.map((subSubjectId) => {
                const subCategory = categoriesList.subSubjects.find(
                  (subSubject) => subSubject.id === subSubjectId,
                );
                return subCategory?.isHideInClient ? (
                  <React.Fragment key={subSubjectId}></React.Fragment>
                ) : (
                  <SubCategoriesListItem
                    key={subSubjectId}
                    subCategory={subCategory as ISubCategory}
                    handleSubCategoryClick={handleSubCategoryClick}
                  />
                );
              })}
            </React.Fragment>
          ))
        )}
        <Button
          variant='outlined'
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: '12px 0',
            width: isDesktop ? '234px' : '208px',
            m: '52px auto 0',
            borderColor: 'primary.main',
            bgcolor: '#fff',
            color: 'primary.main',
            transition: 'stroke 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            '&:hover': {
              path: {
                stroke: '#fff',
              },
            },
            '&:focus': {
              path: {
                stroke: '#fff',
              },
            },
          }}
          onClick={() => goBack()}
        >
          <Avatar
            src={NewRightArrow}
            alt=''
            sx={{
              width: '20px',
              height: '20px',
              ml: '4px',
            }}
          />
          <Typography variant={'body4'}>
            {Dictionary[DICTIONARY.General].back}
          </Typography>
        </Button>
      </Stack>
    </>
  );
};
